import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Loadable from 'react-loadable';

import disableScreenScrolling from '@stubhub/react-disable-screen-scrolling';
import {getPrimaryEventUrl, getTicketBisEventUrl, isPrimaryEvent, isTicketBisEvent} from '@stubhub/react-event-helper';
import FormattedLink from '@stubhub/react-formatted-link';

import EventRedirectionUtil from './eventRedirectionUtil';


const TicketBisDialog = Loadable({
  loader: () => import(/* webpackChunkName: "swi-TicketBisDialog" */ '@stubhub/react-ticketbis-dialog'),
  loading: () => null,
});
const PrimaryDialog = Loadable({
  loader: () => import(/* webpackChunkName: "swi-PrimaryDialog" */ '@stubhub/react-primary-dialog'),
  loading: () => null,
});
const PopUpModal = Loadable({
  loader: () => import(/* webpackChunkName: "swi-PopUpModal" */ '@stubhub/react-popup-modal'),
  loading: () => null,
});
const RedirectModal = Loadable({
  loader: () => import(/* webpackChunkName: "swi-RedirectModal" */ '@stubhub/react-redirect-modal'),
  loading: () => null,
});
const TicketBisDialogCmp = disableScreenScrolling(TicketBisDialog);
const PrimaryDialogCmp = disableScreenScrolling(PrimaryDialog);
const PopUpModalCmp = disableScreenScrolling(PopUpModal);

class EventRedirection extends Component {
  constructor(props, ctx) {
    super(props, ctx);
    this.state = {
      showTicketBisDialog: false,
      showPopUpModal: false,
      showPrimaryDialog: false,
      showRedirectModal: false,
    };
    this.eventRedirectionUtil = new EventRedirectionUtil(this.context.globalRegistry.getRedirectionRules());
  }

  /**
   * Function to Handle Click on Events Cards and Links
   * @param evt React Event Object for the `click` action.
   * TODO - Add Tracking Call for button click
   */
  navigateToEvent = (evt) => {
    let {onClick, event} = this.props;

    onClick && onClick(evt);
    evt && evt.stopPropagation();

    let prevent = true;
    if (isTicketBisEvent(event)) {
      this.setState({
        showTicketBisDialog: true,
      });
    } else if (isPrimaryEvent(event)) {
      this.setState({
        showPrimaryDialog: true,
      });
    } else if (this.isCMAWebUri()) {
      this.setState({
        showRedirectModal: true,
      });
    } else if (!this.eventRedirectionUtil.sameSite(event, this.context.globalRegistry.getDefaultWebTLD())) {
      this.setState({
        showPopUpModal: true,
      });
    } else {
      prevent = false;
    }
    if (prevent) {
      evt && evt.preventDefault();
    }
  };

  isCMAWebUri = () => {
    const {globalRegistry} = this.context;

    if (globalRegistry && globalRegistry.getFeatureValue('common.redirectModal.showRedirectModal')) {
      const {event} = this.props;
      const domain = globalRegistry.getDefaultWebTLD();

      return domain === 'co.uk' && event && event.venue && event.venue.country !== 'GB';
    }

    return false;
  };

  onCancel = () => {
    // OnCancel is function within react-performer-card which set state show:false
    this.props.onCancel && this.props.onCancel();
  };

  /**
   * Close popup modal
   */
  onClosePopUpModal = () => {
    this.setState({
      showPopUpModal: false,
    });
    this.onCancel();
  };

  /**
   * Close redirect modal
   */
  onCloseRedirectModal = () => {
    this.setState({
      showRedirectModal: false,
    });
  };

  /**
   * Close TicketBisDialog
   */
  onCloseTicketBisDialog = () => {
    this.setState({
      showTicketBisDialog: false,
    });
    this.onCancel();
  };
  onClosePrimaryDialog = () => {
    this.setState({
      showPrimaryDialog: false,
    });
    this.onCancel();
  };

  componentDidMount() {
    if (this.props.auto) {
      this.navigateToEvent();
    }
  }

  componentDidUpdate() {
    const {showTicketBisDialog, showPrimaryDialog, showPopUpModal, showRedirectModal} = this.state;
    if (this.props.auto && !showTicketBisDialog && !showPrimaryDialog && !showPopUpModal && !showRedirectModal) {
      this.navigateToEvent();
    }
  }

  render() {
    let {event, children, className, disableClose} = this.props;
    let {webURI, name} = this.props.event;
    let ticketInfo = event.attributes && event.attributes.ticketInfo ? event.attributes.ticketInfo : event.ticketInfo;
    let currency = event.currency || event.currencyCode || (ticketInfo && ticketInfo.currencyCode);

    let defaultWebTLD = this.context.globalRegistry.getDefaultWebTLD();
    let redirectionDomain = this.context.globalRegistry.getPropertyValue(`tb.eventpage.url.domain.${defaultWebTLD}`);
    let cmaURL = __CLIENT__ && `${window.location.origin.replace('.co.uk', '.ie')}/${event.webURI}`;

    const isTBEvent = isTicketBisEvent(event);
    if (isTBEvent) {
      webURI = getTicketBisEventUrl(event, redirectionDomain, defaultWebTLD);
    } else if (isPrimaryEvent(event)) {
      webURI = getPrimaryEventUrl(event);
    }

    if (isTBEvent && this.isCMAWebUri()) {
      webURI = webURI.replace('.co.uk', '.ie');
    }

    return (
      <div className="EventRedirection">
        {this.state.showRedirectModal && (
          <RedirectModal webURI={cmaURL} onClose={this.onCloseRedirectModal} disableClose={disableClose} />
        )}
        {this.state.showPopUpModal && (
          <PopUpModalCmp webURI={webURI} currency={currency} onClose={this.onClosePopUpModal} />
        )}
        {this.state.showTicketBisDialog && (
          <TicketBisDialogCmp webURI={webURI} onClose={this.onCloseTicketBisDialog} isCMA={this.isCMAWebUri()} />
        )}
        {this.state.showPrimaryDialog && <PrimaryDialogCmp webURI={webURI} onClose={this.onClosePrimaryDialog} />}
        {!this.props.auto && (
          <FormattedLink href={webURI} className={className} onClick={(e) => this.navigateToEvent(e)}>
            {children ? children : name}
          </FormattedLink>
        )}
      </div>
    );
  }
}

EventRedirection.contextTypes = {
  globalRegistry: PropTypes.object,
  router: PropTypes.object,
};

EventRedirection.propTypes = {
  event: PropTypes.object.isRequired,
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  onCancel: PropTypes.func,
  disableClose: PropTypes.bool,
  auto: PropTypes.bool,
};

export default EventRedirection;
